import { useState } from "react";
import { Minus, Plus } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import type { CatalogFacetCounts, CdnCategory } from "@/lib/cdnProducts";

export interface CatalogFilterState {
  category: string;
  vendor: string;
  series: string[];
  inStock: boolean;
  priceMin: string;
  priceMax: string;
}

export const EMPTY_FILTERS: CatalogFilterState = {
  category: "",
  vendor: "",
  series: [],
  inStock: false,
  priceMin: "",
  priceMax: "",
};

export function countActiveFilters(f: CatalogFilterState): number {
  return (
    (f.category ? 1 : 0) +
    (f.vendor ? 1 : 0) +
    f.series.length +
    (f.inStock ? 1 : 0) +
    (f.priceMin ? 1 : 0) +
    (f.priceMax ? 1 : 0)
  );
}

function Section({
  title,
  children,
  defaultOpen = true,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-border py-4">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between text-left"
      >
        <span className="text-sm font-semibold text-foreground">{title}</span>
        {open ? (
          <Minus className="h-4 w-4 text-muted-foreground" />
        ) : (
          <Plus className="h-4 w-4 text-muted-foreground" />
        )}
      </button>
      {open && <div className="mt-3 space-y-2.5">{children}</div>}
    </div>
  );
}

function OptionList({
  options,
  selected,
  onToggle,
  idPrefix,
  initial = 8,
}: {
  options: CdnCategory[];
  selected: string[];
  onToggle: (name: string) => void;
  idPrefix: string;
  initial?: number;
}) {
  const [expanded, setExpanded] = useState(false);
  if (options.length === 0) {
    return <p className="text-xs text-muted-foreground">No options</p>;
  }
  const shown = expanded ? options : options.slice(0, initial);
  return (
    <>
      {shown.map((opt) => {
        const id = `${idPrefix}-${opt.name}`;
        return (
          <label
            key={id}
            htmlFor={id}
            className="flex cursor-pointer items-start justify-between gap-3 text-sm"
          >
            <span className="flex items-start gap-2.5">
              <Checkbox
                id={id}
                className="mt-0.5"
                checked={selected.includes(opt.name)}
                onCheckedChange={() => onToggle(opt.name)}
              />
              <span className="text-foreground">{opt.name}</span>
            </span>
            <span className="shrink-0 text-muted-foreground">({opt.count})</span>
          </label>
        );
      })}
      {options.length > initial && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-sm text-primary underline underline-offset-2"
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </>
  );
}

interface Props {
  value: CatalogFilterState;
  onChange: (next: CatalogFilterState) => void;
  facets?: CatalogFacetCounts;
  loading?: boolean;
  /** Hide the Category section (e.g. on a category landing page). */
  hideCategory?: boolean;
  /** Hide the Brand section (e.g. on a manufacturer landing page). */
  hideVendor?: boolean;
}

export default function CatalogFilterRail({
  value,
  onChange,
  facets,
  loading,
  hideCategory,
  hideVendor,
}: Props) {
  const set = (patch: Partial<CatalogFilterState>) => onChange({ ...value, ...patch });
  const inStockCount = facets?.total ?? 0;

  return (
    <div className="text-sm">
      <Section title="Availability">
        <label htmlFor="filter-instock" className="flex cursor-pointer items-center justify-between gap-3">
          <span className="flex items-center gap-2.5">
            <Checkbox
              id="filter-instock"
              checked={value.inStock}
              onCheckedChange={(c) => set({ inStock: !!c })}
            />
            <span className="text-foreground">In stock</span>
          </span>
          {value.inStock && <span className="text-muted-foreground">({inStockCount})</span>}
        </label>
      </Section>

      <Section title="Price">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              $
            </span>
            <Input
              inputMode="decimal"
              placeholder="0"
              aria-label="Minimum price"
              className="h-10 pl-7"
              value={value.priceMin}
              onChange={(e) => set({ priceMin: e.target.value.replace(/[^\d.]/g, "") })}
            />
          </div>
          <span className="text-muted-foreground">-</span>
          <div className="relative flex-1">
            <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
              $
            </span>
            <Input
              inputMode="decimal"
              placeholder="Max"
              aria-label="Maximum price"
              className="h-10 pl-7"
              value={value.priceMax}
              onChange={(e) => set({ priceMax: e.target.value.replace(/[^\d.]/g, "") })}
            />
          </div>
        </div>
      </Section>

      {!hideCategory && (
        <Section title="Category">
          <OptionList
            idPrefix="cat"
            options={facets?.categories ?? []}
            selected={value.category ? [value.category] : []}
            onToggle={(name) => set({ category: value.category === name ? "" : name })}
          />
        </Section>
      )}

      {!hideVendor && (
        <Section title="Brand">
          <OptionList
            idPrefix="brand"
            options={facets?.vendors ?? []}
            selected={value.vendor ? [value.vendor] : []}
            onToggle={(name) => set({ vendor: value.vendor === name ? "" : name })}
          />
        </Section>
      )}

      <Section title="Product Series">
        <OptionList
          idPrefix="series"
          options={facets?.series ?? []}
          selected={value.series}
          onToggle={(name) =>
            set({
              series: value.series.includes(name)
                ? value.series.filter((s) => s !== name)
                : [...value.series, name],
            })
          }
        />
      </Section>

      {loading && <p className="py-3 text-xs text-muted-foreground">Updating counts…</p>}
    </div>
  );
}